var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * CZNA-1344 — "Průběh importu": how each stage of one import went.
 *
 * Every stage of `ImportService.processImport` is listed, in that order, including the
 * ones that did not run — a skipped or failed stage shows why, it does not disappear.
 * Stage names reuse the texts of the import description (CZNA-1343); result keys and
 * values without a text render under their code rather than vanishing.
 *
 * The derivation runs below are the very rows the derivation run history shows
 * (filed with this import's id), one per vocabulary rule the import's pass ran.
 */
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Headline, Loading, MessageBanner } from '@folio/stripes/components';
import useImportBatchProtocol from '../../hooks/useImportBatchProtocol';
import { useVocabularyTypes } from '../../hooks/useVocabularyTypes';
var CELL_STYLE = {
    textAlign: 'left',
    padding: '4px 8px',
    borderBottom: '1px solid #e0e0e0',
    verticalAlign: 'top',
};
var NUMBER_CELL_STYLE = __assign(__assign({}, CELL_STYLE), { textAlign: 'right' });
var STATUS_COLORS = {
    COMPLETED: { bg: '#e6f4ea', fg: '#1e7e34' },
    DISPATCHED: { bg: '#e6f4ea', fg: '#1e7e34' },
    RUNNING: { bg: '#e7f1fb', fg: '#1a5d9c' },
    FAILED: { bg: '#fbe9e9', fg: '#a12a2a' },
    SKIPPED: { bg: '#f1f1f1', fg: '#555555' },
};
/** Results that are plumbing, not an answer: the job id, the stage a skip points at. */
var HIDDEN_RESULTS = new Set(['revalidationJobId', 'failedPhase', 'jobState', 'deriveOutcomeKnown']);
var ImportProtocolSection = function (_a) {
    var _b;
    var batchId = _a.batchId;
    var intl = useIntl();
    var _c = useImportBatchProtocol(batchId), data = _c.data, isLoading = _c.isLoading, isError = _c.isError;
    var hasRuns = ((_b = data === null || data === void 0 ? void 0 : data.derivationRuns.length) !== null && _b !== void 0 ? _b : 0) > 0;
    var vocabularyTypes = useVocabularyTypes({ enabled: hasRuns }).vocabularyTypes;
    var key = function (id) { return "ui-czna-authorities.imports.protocol.".concat(id); };
    var hasText = function (id) { var _a; return !!((_a = intl.messages) === null || _a === void 0 ? void 0 : _a[id]); };
    var textOr = function (id, fallback, values) { return (hasText(id) ? intl.formatMessage({ id: id }, values) : fallback); };
    var vocabularyLabel = function (ruleId) {
        var _a, _b;
        var code = ruleId ? (_a = data === null || data === void 0 ? void 0 : data.ruleVocabularyTypes) === null || _a === void 0 ? void 0 : _a[ruleId] : undefined;
        if (!code)
            return ruleId !== null && ruleId !== void 0 ? ruleId : '—';
        var type = vocabularyTypes.find(function (vt) { return vt.code === code; });
        if (!type)
            return code;
        return ((_b = intl.locale) !== null && _b !== void 0 ? _b : '').toLowerCase().startsWith('cs') ? type.labelCs : (type.labelEn || type.labelCs);
    };
    var dateTime = function (value) { return (value
        ? "".concat(intl.formatDate(value, { day: '2-digit', month: '2-digit', year: 'numeric' }), " ").concat(intl.formatTime(value, { hour: '2-digit', minute: '2-digit', second: '2-digit' }))
        : '—'); };
    var formatValue = function (value) {
        if (Array.isArray(value))
            return value.map(formatValue).join(', ');
        if (typeof value === 'number')
            return intl.formatNumber(value);
        if (typeof value === 'boolean')
            return intl.formatMessage({ id: "ui-czna-authorities.imports.pipeline.".concat(value ? 'yes' : 'no') });
        if (value == null)
            return '—';
        var text = String(value);
        return textOr(key("value.".concat(text)), text);
    };
    var renderResults = function (phase) {
        var _a;
        var entries = Object.entries((_a = phase.results) !== null && _a !== void 0 ? _a : {}).filter(function (_a) {
            var k = _a[0];
            return !HIDDEN_RESULTS.has(k);
        });
        if (entries.length === 0)
            return '—';
        return (React.createElement("ul", { style: { margin: 0, paddingLeft: '1rem' } }, entries.map(function (_a) {
            var k = _a[0], v = _a[1];
            return (React.createElement("li", { key: k, "data-testid": "import-protocol-result-".concat(phase.code, "-").concat(k) },
                textOr(key("result.".concat(k)), k),
                ": ",
                React.createElement("strong", null, formatValue(v))));
        })));
    };
    var renderReason = function (phase) {
        var _a;
        if (!phase.reasonCode && !phase.detail)
            return '—';
        var failedPhase = (_a = phase.results) === null || _a === void 0 ? void 0 : _a.failedPhase;
        return (React.createElement(React.Fragment, null,
            phase.reasonCode && (React.createElement("div", null, textOr(key("reason.".concat(phase.reasonCode)), phase.reasonCode, {
                phase: failedPhase
                    ? textOr("ui-czna-authorities.imports.pipeline.phase.".concat(failedPhase, ".title"), failedPhase)
                    : '—',
            }))),
            phase.detail && (React.createElement("div", { style: { fontFamily: 'monospace', fontSize: '0.85em', wordBreak: 'break-word' } }, phase.detail))));
    };
    var renderStatus = function (status) {
        var _a;
        var colors = (_a = STATUS_COLORS[status]) !== null && _a !== void 0 ? _a : { bg: '#f1f1f1', fg: '#555555' };
        return (React.createElement("span", { style: {
                display: 'inline-block',
                backgroundColor: colors.bg,
                color: colors.fg,
                borderRadius: '10px',
                padding: '0 8px',
                fontSize: '0.85em',
                fontWeight: 600,
                whiteSpace: 'nowrap',
            } }, textOr(key("status.".concat(status)), status)));
    };
    var renderRuns = function (runs) {
        if (runs.length === 0) {
            return React.createElement("p", { "data-testid": "import-protocol-no-runs" },
                React.createElement(FormattedMessage, { id: key('runs.empty') }));
        }
        var column = function (id) { return intl.formatMessage({ id: "ui-czna-authorities.derivationRules.runs.column.".concat(id) }); };
        return (React.createElement("table", { style: { borderCollapse: 'collapse', width: '100%' }, "data-testid": "import-protocol-runs" },
            React.createElement("thead", null,
                React.createElement("tr", null,
                    React.createElement("th", { style: CELL_STYLE }, intl.formatMessage({ id: 'ui-czna-authorities.imports.pipeline.rules.column.vocabulary' })),
                    React.createElement("th", { style: CELL_STYLE }, column('startedAt')),
                    React.createElement("th", { style: NUMBER_CELL_STYLE }, column('termsCreated')),
                    React.createElement("th", { style: NUMBER_CELL_STYLE }, column('termsAlreadyPresent')),
                    React.createElement("th", { style: NUMBER_CELL_STYLE }, column('componentsRematched')),
                    React.createElement("th", { style: CELL_STYLE }, intl.formatMessage({ id: key('runs.column.error') })))),
            React.createElement("tbody", null, runs.map(function (run) { return (React.createElement("tr", { key: run.id, "data-testid": "import-protocol-run-".concat(run.id) },
                React.createElement("td", { style: CELL_STYLE }, vocabularyLabel(run.ruleId)),
                React.createElement("td", { style: CELL_STYLE }, dateTime(run.startedAt)),
                React.createElement("td", { style: NUMBER_CELL_STYLE, "data-testid": "import-protocol-run-created-".concat(run.id) }, intl.formatNumber(run.termsCreated)),
                React.createElement("td", { style: NUMBER_CELL_STYLE }, intl.formatNumber(run.termsAlreadyPresent)),
                React.createElement("td", { style: NUMBER_CELL_STYLE }, intl.formatNumber(run.componentsRematched)),
                React.createElement("td", { style: CELL_STYLE }, run.errorMessage || '—'))); }))));
    };
    var renderBody = function () {
        if (isLoading)
            return React.createElement(Loading, null);
        if (isError || !data) {
            return React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: key('loadError') }));
        }
        return (React.createElement(React.Fragment, null,
            !data.recorded && (React.createElement(MessageBanner, { type: "warning" },
                React.createElement("span", { "data-testid": "import-protocol-not-recorded" },
                    React.createElement(FormattedMessage, { id: key('notRecorded') })))),
            React.createElement("table", { style: { borderCollapse: 'collapse', width: '100%' }, "data-testid": "import-protocol-phases" },
                React.createElement("thead", null,
                    React.createElement("tr", null, ['phase', 'status', 'startedAt', 'finishedAt', 'results', 'reason'].map(function (col) { return (React.createElement("th", { key: col, style: CELL_STYLE }, intl.formatMessage({ id: key("column.".concat(col)) }))); }))),
                React.createElement("tbody", null, data.phases.map(function (phase) { return (React.createElement("tr", { key: phase.code, "data-testid": "import-protocol-phase-".concat(phase.code) },
                    React.createElement("td", { style: CELL_STYLE }, textOr("ui-czna-authorities.imports.pipeline.phase.".concat(phase.code, ".title"), phase.code)),
                    React.createElement("td", { style: CELL_STYLE, "data-testid": "import-protocol-status-".concat(phase.code) }, renderStatus(phase.status)),
                    React.createElement("td", { style: CELL_STYLE }, dateTime(phase.startedAt)),
                    React.createElement("td", { style: CELL_STYLE }, dateTime(phase.finishedAt)),
                    React.createElement("td", { style: CELL_STYLE }, renderResults(phase)),
                    React.createElement("td", { style: CELL_STYLE, "data-testid": "import-protocol-reason-".concat(phase.code) }, renderReason(phase)))); }))),
            React.createElement(Headline, { size: "medium", margin: "medium" },
                React.createElement(FormattedMessage, { id: key('runs.title') })),
            renderRuns(data.derivationRuns)));
    };
    return (React.createElement("section", { "data-testid": "import-protocol" },
        React.createElement(Headline, { size: "large", margin: "small" },
            React.createElement(FormattedMessage, { id: key('title') })),
        renderBody()));
};
export default ImportProtocolSection;
